import { useContext, useEffect, useState } from "react";
import { IQuestion } from "../types";
import { SettingsContext } from "../settings/settingsProvider";
import { Fault } from "./fault";
import { markFaulty, useAnalytics } from "../firebase/firebase";
import { LocationContext } from "../nav/pageChooser";
import { useIsMobile } from "../device/useIsMobile";
import { TotalMarks } from "./totalMarks";
import { SelectedItemsContext } from "../selected/selectedItemsProvider";
import { useContextWithGuard } from "../helpers/useContextWithGuard";
import { Brains } from "./brains";
import { ScrollImageButtons } from "./scrollImageButton";
import { ScribbleButton } from "./scribbleButton";
import { ToggleFavouritesButton } from "./ToggleFavouritesButton";
import ScaledImage from "./scaledImage";
import { Paper, Theme, useTheme } from "@mui/material";
import { ItemMenu } from "./itemMenu";
import { ShareToMarkupButton } from "./shareToMarkupButton";
import { useCanSharePdf } from "./useCanSharePdf";

type QuestionPaperProps = {
    question: IQuestion;
    showInfoButton: React.ReactNode;
};

const questionPaperStyle: React.CSSProperties = {
    margin: "2vh auto",
    borderRadius: "10px",
    borderWidth: "2px",
    position: "relative",
    padding: "0",
    borderStyle: "solid",
}

const itemVerticalMenuStyle: React.CSSProperties = {
    position: "absolute",
    right: "10px",
    bottom: "80px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "fit-content",
    gap: "2",
    marginLeft: "10px"
}

const scrollingContainerStyle: React.CSSProperties = {
    height: "100%",
    width: "100%",
    overflowY: "scroll",
    overflowX: "hidden",
    borderRadius: "10px"
}

const itemHzMenuStyle: React.CSSProperties = {
    position: "absolute",
    bottom: "10px",
    right: "10px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    gap: "2",
    backgroundColor: "#fff",
    borderRadius: "50vh",
    borderStyle: "solid",
    width: "fit-content",
    marginLeft: "10px"
}


export const QuestionPaper = (props: QuestionPaperProps) => {
    const { track } = useAnalytics();
    const theme = useTheme<Theme>();
    const { questionHeight, columnsOverride, imageScale } = useContextWithGuard(SettingsContext, "SettingsContext");
    const selectedItemsContext = useContext(SelectedItemsContext);
    const selectedQns = selectedItemsContext?.selectedQns;
    const addToSelected = selectedItemsContext?.addToSelected;
    const removeFromSelected = selectedItemsContext?.removeFromSelected;
    const { collection } = useContextWithGuard(LocationContext, "LocationContext");

    const [isSelected, setIsSelected] = useState<boolean>(false);
    const [isFaulty, setIsFaulty] = useState<boolean>(props.question?.fault_clicks > 0);
    const isMobile = useIsMobile();
    const canSharePdf = useCanSharePdf();
    
    const [index, setIndex] = useState<number>(0);
    const [src, setSrc] = useState<string>(props.question?.qp_img_urls ? props.question.qp_img_urls[0] : "")


    const clickHandler = () => {
        if (addToSelected && removeFromSelected) {
            if (!isSelected) {
                addToSelected([props.question]);
            }
            else {
                removeFromSelected(props.question);
            }
        }
    }

    useEffect(() => {
        if (setIsSelected && selectedQns) {
            setIsSelected(selectedQns.indexOf(props.question) !== -1)
        }
    }, [JSON.stringify(selectedQns), props.question])

    const faultClickHandler = () => {
        if (props.question && !isFaulty) {
            setIsFaulty(true);
            markFaulty(collection, props.question.id);
            track("item.fault.add", { id: props.question.id, collection: props.question.collection })
        }
    }

    const nextImage = () => {
        if (props.question && index < props.question.qp_num_pages + props.question.ms_num_pages - 1) {
            setIndex(index + 1)
        }
    }

    const prevImage = () => {
        if (index > 0) {
            setIndex(index - 1)
        }
    }

    useEffect(() => {
        if (props.question) {
            if (index < props.question.qp_num_pages) {
                setSrc(props.question.qp_img_urls[index])
            }
            else {
                setSrc(props.question.ms_img_urls[index - props.question.qp_num_pages])
            }
        }
    }, [index, props.question])

    const oneColumn = columnsOverride === 0 ? isMobile : columnsOverride === 1;

    return (
        props.question &&
        <Paper
            onClick={clickHandler}
            sx={{
                height: `${questionHeight}vh`,
                width: oneColumn ? "90vw" : "45vw",
                borderColor: isSelected ? theme.bee.highlightBorder : "transparent",
                ...questionPaperStyle
            }}
        >
            <div style={scrollingContainerStyle}>
                <ScaledImage
                    src={src}
                    scale={imageScale}
                    alt={props.question.text}
                />
            </div>

            <div style={itemVerticalMenuStyle}>
                <Brains difficulty={props.question.difficulty} />
            </div>

            <div style={{
                ...itemHzMenuStyle,
                borderColor: theme.bee.darkBorder,
                backgroundColor: theme.bee.barBackgroundLight,
            }}>
                <ScrollImageButtons
                    onLeft={prevImage}
                    onRight={nextImage}
                    outOf={props.question.qp_num_pages + props.question.ms_num_pages}
                    index={index} />
                {/* {
                    props.showInfoButton
                } */}
                {
                    canSharePdf
                        ?
                        <ShareToMarkupButton pdfUrl={props.question.qp_pdf_url} />
                        :
                        <ScribbleButton question={props.question} />
                }
                <ToggleFavouritesButton question={props.question} />
                {/* <Fault
                    isFaulty={isFaulty}
                    toggleIsFaulty={faultClickHandler}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)} /> */}
                <TotalMarks outOf={props.question.num_marks} isBottomCorner={false} />
                <ItemMenu item={props.question} />
            </div>
        </Paper>
    );
};
